import type { SyncableTable } from './action'

/**
 * A summary of what the client holds for one table, small enough to fit in a request no
 * matter how large the membership is.
 *
 * The full reconcile used to upload every `[id, updated_at]` the client held. That request
 * grew with the membership — ~68 bytes a row — and AWS WAF blocks bodies over 8 KB on an
 * ALB, a limit that is fixed and cannot be raised. At ~120 rows across all thirteen tables
 * the manifest stopped fitting, which is nearly every real member.
 *
 * So the client sends a digest instead, and the server sends *every row* of the tables that
 * disagree back down. Responses are not size-limited; requests are. The governing rule for
 * this protocol is that the client never enumerates its rows to the server.
 *
 * See app/Sync/SyncDigest.php for the twin, and __fixtures__/digest for what keeps them
 * agreeing.
 */
export type TableDigest = {
  /** How many rows the client holds. A cheap discriminator alongside the hash. */
  n: number
  /** 16 lowercase hex characters: two 32-bit FNV-1a lanes. */
  h: string
}

export type SyncDigestPayload = Partial<Record<SyncableTable, TableDigest>>

const FNV_PRIME = 16777619

/**
 * Two lanes, so a collision has to happen in both at once. The first basis is FNV-1a's
 * standard 32-bit offset; the second is that value byte-swapped — arbitrary, but fixed and
 * mirrored in PHP.
 *
 * Two 32-bit lanes rather than one 64-bit hash because JavaScript integers are only exact to
 * 53 bits. `Math.imul` keeps each lane exact without reaching for BigInt.
 */
const LANE_A_BASIS = 0x811c9dc5
const LANE_B_BASIS = 0x9dc5811c

const encoder = new TextEncoder()

function fnv1a(bytes: Uint8Array, basis: number): number {
  let hash = basis

  for (let i = 0; i < bytes.length; i++) {
    hash ^= bytes[i]
    // Exact mod 2^32 throughout. The intermediate is a signed int32; `>>> 0` at the end is
    // what makes it unsigned, and PHP masks with 0xFFFFFFFF to the same effect.
    hash = Math.imul(hash, FNV_PRIME)
  }

  return hash >>> 0
}

function hex8(value: number): string {
  return (value >>> 0).toString(16).padStart(8, '0')
}

/**
 * The digest of one table.
 *
 * Deduped by id, last write winning, for two reasons. It matches what the server's manifest
 * index does implicitly (`$indexed[$table][$id] = $ts`), and `deletedLocally` has no dedupe
 * on push — a row deleted twice is appended twice, and an XOR fold would cancel it out
 * entirely, hiding the row from the digest instead of reporting it.
 *
 * The fold is XOR precisely so neither side has to sort. Row order is not part of the
 * result.
 *
 * No special case for an empty store. It diverges on all thirteen tables and the server
 * answers with the whole membership in that one response — which is exactly what a
 * `since: null` shortcut would have fetched, for the cost of a ~550 byte request.
 */
export function digestOf(rows: Iterable<[string, string]>): TableDigest {
  const deduped = new Map<string, string>()
  for (const [id, updatedAt] of rows) {
    deduped.set(id, updatedAt)
  }

  let foldA = 0
  let foldB = 0

  for (const [id, updatedAt] of deduped) {
    // UTF-8 bytes, explicitly, so the two implementations cannot disagree about encoding.
    // In practice these are UUIDs and ISO-8601 timestamps, so every byte is ASCII.
    const bytes = encoder.encode(`${id}|${updatedAt}`)
    foldA = (foldA ^ fnv1a(bytes, LANE_A_BASIS)) >>> 0
    foldB = (foldB ^ fnv1a(bytes, LANE_B_BASIS)) >>> 0
  }

  return { n: deduped.size, h: hex8(foldA) + hex8(foldB) }
}
